// Baseline sessions seeded from last-known working weights (gym-tracker-spec.md
// section 10), so "beat this" targets and the progression engine work on day one
// before any real logging or Notes import. These are explicitly marked as seed
// baselines (sessionNote) and can be deleted once real history exists.
//
// All weights TOTAL kg. Dumbbell entries already doubled from the per-hand numbers
// in section 10 (per Marko's confirmed convention). Last working set of each
// exercise is taken to failure (rir 0) per the methodology; balance-dependent
// movements stay at 1 RIR.

import type { Feel, GripVariant, Session, SetLog, ExerciseEntry } from '../domain/types';
import { toISODate } from '../domain/volume';
import { DEFAULT_GYM_ID } from '../domain/gym';

interface BSet {
  weightKg: number;
  reps: number;
  rir: number | null;
  warmup?: boolean;
}
interface BEntry {
  exerciseId: string;
  sets: BSet[];
  elbowFeel?: Feel;
  forearmFeel?: Feel;
  gripVariant?: GripVariant;
}
interface Baseline {
  routineId: string;
  routineLabel: string;
  daysAgo: number;
  bodyweightKg: number;
  entries: BEntry[];
}

// Straight working sets at one weight: all but the last at 1 RIR, last to failure.
function straight(weightKg: number, reps: number[], rampWeights: number[] = []): BSet[] {
  const sets: BSet[] = rampWeights.map((w) => ({ weightKg: w, reps: 10, rir: null, warmup: true }));
  reps.forEach((r, i) => sets.push({ weightKg, reps: r, rir: i === reps.length - 1 ? 0 : 1 }));
  return sets;
}

const BASELINES: Baseline[] = [
  {
    routineId: 'upper-a',
    routineLabel: 'Upper A (push + back)',
    daysAgo: 8,
    bodyweightKg: 80.5,
    entries: [
      { exerciseId: 'dips', sets: [{ weightKg: 6, reps: 10, rir: 0 }, { weightKg: 6, reps: 8, rir: 0 }, { weightKg: 6, reps: 7, rir: 0 }] },
      { exerciseId: 'row-machine', sets: straight(50, [13, 13, 13]), elbowFeel: 'none' },
      { exerciseId: 'bench-machine', sets: straight(27.5, [12, 10, 9], [15, 22.5]) },
      { exerciseId: 'db-ohp', sets: straight(30, [12, 10, 9]) },
      { exerciseId: 'side-raise-db', sets: straight(14, [14, 13, 12]) },
      { exerciseId: 'leg-extension-downstairs', sets: straight(42.5, [14, 13, 13]) },
    ],
  },
  {
    routineId: 'upper-b',
    routineLabel: 'Upper B (pull + chest)',
    daysAgo: 7,
    bodyweightKg: 80.5,
    entries: [
      { exerciseId: 'lat-pulldown', sets: straight(57.5, [14, 14, 13], [30, 45]), elbowFeel: 'none' },
      { exerciseId: 'chest-supported-row', sets: straight(70, [10, 10, 11]), elbowFeel: 'none' },
      { exerciseId: 'straight-arm-pulldown', sets: straight(23, [14, 13, 12]), elbowFeel: 'none' },
      { exerciseId: 'incline-db-press', sets: straight(40, [13, 12, 12]) },
      { exerciseId: 'biceps-curl', sets: straight(20, [12, 11, 10]), forearmFeel: 'none', gripVariant: 'supinated' },
      { exerciseId: 'seated-leg-curl', sets: straight(37.5, [14, 13, 13]) },
    ],
  },
  {
    routineId: 'arms-delts',
    routineLabel: 'Arms + Delts (weak-point day)',
    daysAgo: 5,
    bodyweightKg: 80.5,
    entries: [
      { exerciseId: 'biceps-curl', sets: straight(20, [12, 11, 10]), forearmFeel: 'none', gripVariant: 'hammer' },
      { exerciseId: 'tricep-pushdown', sets: straight(21, [12, 11, 10]) },
      { exerciseId: 'side-raise-machine', sets: straight(30, [14, 13, 12]) },
      { exerciseId: 'butterfly-reverse', sets: straight(25, [14, 13, 12]) },
      { exerciseId: 'incline-db-curl', sets: straight(20, [12, 11, 10]), forearmFeel: 'none', gripVariant: 'neutral' },
      { exerciseId: 'leg-extension-downstairs', sets: straight(42.5, [14, 13, 13]) },
    ],
  },
  {
    routineId: 'legs',
    routineLabel: 'Legs (full)',
    daysAgo: 4,
    bodyweightKg: 80.5,
    entries: [
      {
        exerciseId: 'leg-press',
        sets: [
          { weightKg: 100, reps: 10, rir: null, warmup: true },
          { weightKg: 140, reps: 6, rir: null, warmup: true },
          { weightKg: 180, reps: 7, rir: 1 }, // heavy top set, never to failure
          { weightKg: 160, reps: 12, rir: 1 },
          { weightKg: 160, reps: 10, rir: 0 },
        ],
      },
      { exerciseId: 'nordic-curl', sets: [{ weightKg: 0, reps: 9, rir: 0 }, { weightKg: 0, reps: 8, rir: 0 }, { weightKg: 0, reps: 7, rir: 0 }] },
      { exerciseId: 'bulgarian-split-squat', sets: [{ weightKg: 30, reps: 10, rir: 1 }, { weightKg: 30, reps: 9, rir: 1 }, { weightKg: 30, reps: 9, rir: 1 }] },
      { exerciseId: 'lying-leg-curl', sets: straight(15, [14, 13, 13]) },
      { exerciseId: 'leg-extension', sets: straight(70, [14, 13, 13]) },
      { exerciseId: 'back-extension', sets: [{ weightKg: 0, reps: 15, rir: 1 }, { weightKg: 0, reps: 15, rir: 1 }, { weightKg: 0, reps: 15, rir: 1 }] },
      { exerciseId: 'captains-chair-raise', sets: [{ weightKg: 0, reps: 12, rir: 1 }, { weightKg: 0, reps: 11, rir: 1 }, { weightKg: 0, reps: 10, rir: 0 }] },
      { exerciseId: 'plank', sets: [{ weightKg: 0, reps: 45, rir: 1 }, { weightKg: 0, reps: 40, rir: 1 }, { weightKg: 0, reps: 35, rir: 0 }] },
      { exerciseId: 'russian-twist', sets: [{ weightKg: 0, reps: 20, rir: 1 }, { weightKg: 0, reps: 20, rir: 1 }, { weightKg: 0, reps: 20, rir: 1 }] },
    ],
  },
];

function toSetLogs(bsets: BSet[]): SetLog[] {
  return bsets.map((s, i) => ({
    setNumber: i + 1,
    isWarmup: s.warmup ?? false,
    weightKg: s.weightKg,
    reps: s.reps,
    rir: s.warmup ? null : s.rir,
    isFailure: !s.warmup && s.rir === 0,
  }));
}

// Build one Session per routine, dated relative to `today`. Stable ids so a second
// seed run is idempotent.
export function buildBaselineSessions(today: Date): Session[] {
  return BASELINES.map((b) => {
    const d = new Date(today);
    d.setDate(today.getDate() - b.daysAgo);
    const date = toISODate(d);
    const iso = d.toISOString();
    const entries: ExerciseEntry[] = b.entries.map((e, idx) => ({
      exerciseId: e.exerciseId,
      order: idx,
      substitutedFrom: null,
      sets: toSetLogs(e.sets),
      elbowFeel: e.elbowFeel ?? null,
      forearmFeel: e.forearmFeel ?? null,
      gripVariant: e.gripVariant ?? null,
      note: '',
    }));
    return {
      id: `seed-${b.routineId}`,
      date,
      // Baselines are the home-gym numbers (spec section 10).
      gymId: DEFAULT_GYM_ID,
      routineId: b.routineId,
      routineLabel: b.routineLabel,
      bodyweightKg: b.bodyweightKg,
      sessionNote: 'Seed baseline from last-known weights (spec section 10). Delete once real history exists.',
      entries,
      createdAt: iso,
      updatedAt: iso,
      finishedAt: iso,
      coaching: null,
      suggestedAdjustments: null,
      coachedAt: null,
    };
  });
}
